import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { IUser } from 'core-components/sitebuilder/models/IUser'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import Logger from 'src/core/utils/Logger'

import { checkIsUserAllowedByCondition } from './visualAudienceExtendedModule'

interface IVisualAudienceProps {
  component: any
  write?: IAudienceCondition
  read?: IAudienceCondition
}

enum UserRights {
  READ = 'read',
  WRITE = 'write',
}

export interface IAudienceCondition {
  UserRoles?: string[]
  HpOrg?: string[]
  Country?: string[]
  Email?: string[]
  GeoMarket?: string[]
}

interface IAssignRolesResult {
  done: boolean
  value: UserRights[]
}

export const VisualAudienceExtended = <T,>({
  read,
  write,
  component: PassedComponent,
  ...passedComponentProps
}: T & IVisualAudienceProps) => {
  const user = useSelector(selectUser)
  const currentRights = useMemo((): UserRights[] => {
    if (
      (!read && !write) ||
      (read && !Object.keys(read).length && write && !Object.keys(write).length)
    ) {
      Logger.error('Missing conditions for Audience filter.')
      return []
    }

    if (!user.roleIds || !user.roleIds.length) {
      return []
    }

    const isUserSuperAdmin = user.roleIds.some((role: string) => role === 'SuperAdmin')
    if (isUserSuperAdmin) {
      return [UserRights.READ, UserRights.WRITE]
    }

    const skipReadCheck = write && !read
    if (skipReadCheck && write) {
      return setRightsForSkipRead(user, write)
    }
    const onlySuperAdminCanEdit = !write && read
    if (onlySuperAdminCanEdit && read) {
      const res = setRightsForOnlySuperAdminCanEdit(user, read)
      if (res.done) {
        return res.value
      }
    }
    return setRightsForDefaultCase(user, write, read)
  }, [read, write, user])

  if (!currentRights || !currentRights.length) {
    return <></>
  }

  if (currentRights.length === 1) {
    const [currentItem] = currentRights
    if (currentItem === UserRights.WRITE) {
      return <PassedComponent {...passedComponentProps} />
    }
    return <PassedComponent {...passedComponentProps} disabled />
  }
  if (currentRights.length === 2) {
    return <PassedComponent {...passedComponentProps} />
  }

  return <></>
}

const setRightsForSkipRead = (user: IUser, write: IAudienceCondition) => {
  if (checkIsUserAllowedByCondition(user, write)) {
    return [UserRights.READ, UserRights.WRITE]
  }

  return [UserRights.READ]
}

const setRightsForOnlySuperAdminCanEdit = (
  user: IUser,
  read: IAudienceCondition
): IAssignRolesResult => {
  if (checkIsUserAllowedByCondition(user, read)) {
    return { done: true, value: [UserRights.READ] }
  }

  return { done: false, value: [] }
}

const setRightsForDefaultCase = (
  user: IUser,
  write?: IAudienceCondition,
  read?: IAudienceCondition
) => {
  if (write && read) {
    if (checkIsUserAllowedByCondition(user, read)) {
      if (checkIsUserAllowedByCondition(user, write)) {
        return [UserRights.READ, UserRights.WRITE]
      }

      return [UserRights.READ]
    }

    if (checkIsUserAllowedByCondition(user, write)) {
      return [UserRights.WRITE]
    }

    return []
  }

  return []
}
